import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { compact, get, isArray, isEmpty } from 'lodash';
import { GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Tooltip } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  importStudyReportTemplates,
  getStudyReportTemplates,
  removeStudyReportTemplate,
} from './_apiStudyReportTemplate';
import { IStudyReportTemplate } from './_types';
import StudyReportTemplateForm from './StudyReportTemplateForm';
import OverflowedDialog from 'components/Dialog/OverflowedDialog';
import Header from 'components/Header/Header';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import Button from 'components/Buttons/Button';
import useAlerts from 'components/Alerts/useAlerts';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';
import { useEntityRemove } from 'utils/hooks/useEntityRemove';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useWithTitle } from 'utils/hooks/useWithTitle';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useActions } from 'utils/hooks/useActions';

const emptyStudyReportTemplateRow = null;
const muiGridKey = 'studyReportTemplatesMUI';

const StudyReportTemplates: React.FC = () => {
  const { t } = useTranslation('StudyReportTemplates');
  useWithTitle();
  const { toggleLoader } = useAppGlobals();
  const { addSuccessAlert } = useAlerts();
  const { confirmationData } = useAppInfo();
  const { storeConfirmationData } = useActions();
  const [dialogState, setDialogState] = useState<boolean>(false);
  const [studyReportTemplateRow, setStudyReportTemplateRow] = useState<IStudyReportTemplate | null>(
    emptyStudyReportTemplateRow,
  );
  const [allStudyReportTemplates, setAllStudyReportTemplates] = useState<IStudyReportTemplate[]>(
    [],
  );
  const [keywords, setKeywords] = useState<string[]>([]);
  const [contents, setContents] = useState<string[]>([]);

  const loadEntity = async () => {
    toggleLoader();
    try {
      setStudyReportTemplateRow(emptyStudyReportTemplateRow);
      const allStudyReportTemplates = await getStudyReportTemplates();

      setAllStudyReportTemplates(allStudyReportTemplates);

      const keywords: string[] = [];
      const contents: string[] = [];
      allStudyReportTemplates.forEach((studyReportTemplate) => {
        keywords.push(get(studyReportTemplate, 'keyword'));
        contents.push(get(studyReportTemplate, 'content'));
      });
      setKeywords(keywords);
      setContents(contents);
    } catch (e) {
      console.debug(e);
    }
    toggleLoader(false);
  };

  const generateDefaultStudyReportTemplates = async () => {
    toggleLoader();
    const importedStudyReportTemplates = await importStudyReportTemplates();
    if (isArray(importedStudyReportTemplates)) {
      if (importedStudyReportTemplates.length > 0) {
        loadEntity();
      }
      addSuccessAlert(t('templatesSuccessfullyGenerated'));
    }
    toggleLoader(false);
  };

  const { onEntityRemove } = useEntityRemove(removeStudyReportTemplate, t, loadEntity);

  const clearAction = () => storeConfirmationData(null);

  const confirmAction = async () => {
    await onEntityRemove(get(confirmationData, 'id'));
    clearAction();
  };

  const toggleDialog = (state: boolean = false) => {
    setDialogState(state);
  };

  const onEntityEdit = async (row: IStudyReportTemplate) => {
    setStudyReportTemplateRow(row);
    toggleDialog(!isEmpty(row) ? true : false);
  };

  const addItemInDialog = () => {
    setStudyReportTemplateRow(emptyStudyReportTemplateRow);
    toggleDialog(true);
  };

  useEffect(() => {
    loadEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const id = !isEmpty(studyReportTemplateRow)
    ? studyReportTemplateRow.id
    : dialogState
      ? 'create'
      : null;

  const renderedDialog = (
    <OverflowedDialog
      title={t('dialog.studyReportTemplate')}
      open={dialogState}
      onClose={() => {
        toggleDialog(false);
        setStudyReportTemplateRow(emptyStudyReportTemplateRow);
      }}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
      fullWidth={true}
    >
      <StudyReportTemplateForm
        toggleDialog={toggleDialog}
        keyValues={{ keywords, contents }}
        id={id}
        row={studyReportTemplateRow}
        loadEntity={loadEntity}
      />
    </OverflowedDialog>
  );

  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);

  const columns = reorderColumnsByGridSettings(
    injectColumnWidthsIntoColumns(
      compact([
        {
          field: 'actions',
          headerName: t('Grid:actions'),
          type: 'actions',
          hideable: false,
          width: 100,
          renderCell: ({ row }: GridRenderCellParams) => {
            return (
              <>
                <GridActionsCellItem
                  icon={
                    <Tooltip title={t('Grid:edit')}>
                      <EditIcon />
                    </Tooltip>
                  }
                  label={t('Grid:edit')}
                  onClick={() => onEntityEdit(row)}
                  data-pw={`studyReportTemplate-edit-${get(row, 'keyword')}`}
                />
                <GridActionsCellItem
                  icon={
                    <Tooltip title={t('Grid:delete')}>
                      <DeleteIcon />
                    </Tooltip>
                  }
                  label={t('Grid:delete')}
                  onClick={() => {
                    storeConfirmationData({ id: get(row, 'id') });
                  }}
                  data-pw={`studyReportTemplate-delete-${get(row, 'keyword')}`}
                />
              </>
            );
          },
        },
        { field: 'keyword', headerName: t('keyword') },
        { field: 'content', headerName: t('content') },
      ]),
    ),
  );

  const renderedOtherButton = (
    <Button
      variant="contained"
      color="primary"
      onClick={generateDefaultStudyReportTemplates}
      sx={{ ml: 1, mb: 0.5 }}
      data-pw="buttons-split-by"
    >
      <AddIcon sx={{ mr: 1 }} />
      {t('generateDefaultStudyReportTemplates')}
    </Button>
  );

  return (
    <>
      <Header
        title=""
        addItemInDialog={addItemInDialog}
        toggleDialogText={t('add')}
        addObject={renderedOtherButton}
        topMargin={true}
      />
      <MuiGrid
        gridKey={muiGridKey}
        rows={allStudyReportTemplates}
        columns={columns}
        initialSortMode={[{ field: 'keyword', sort: 'asc' }]}
      />
      {renderedDialog}
      {confirmationData && confirmationData.id && !confirmationData.editDialog && (
        <ConfirmationDialog
          title={confirmationData.title ? t(confirmationData.title) : t('Grid:confirmDelete')}
          open={true}
          aria-labelledby="form-dialog-title"
          fullWidth={true}
          cancelAction={clearAction}
          confirmAction={confirmAction}
        />
      )}
    </>
  );
};

export default StudyReportTemplates;
